import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService, TranslationService, UserIdService } from '@spartacus/core';
import { catchError, map, Observable, switchMap } from 'rxjs';
import { PricingStatusReportRequest } from './models/pricing-status.model';
import { failStatus, successStatus } from '../../../../core/models/request-status.model';
import { CreditSummaryRequest } from './models/credit-summary.model';
import { PurchaseSummaryDetailsRequest, PurchaseSummaryReportRequest } from './models/purchase-summary.model';
import {
  PaymentHistoryDetailsRequest,
  PaymentHistoryDetailsRequestConfig,
  PaymentHistoryReportRequest,
  PaymentHistoryRequestConfig,
} from './models/payment-history.model';

@Injectable({
  providedIn: 'root',
})
export class ReportsConnector {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
    private userIdService: UserIdService,
  ) {}

  getPricingStatus(): Observable<PricingStatusReportRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('pricingStatus', { urlParams: { userId } });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                report: response,
              },
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  getPurchaseSummary(): Observable<PurchaseSummaryReportRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('purchaseSummary', { urlParams: { userId } });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                report: response,
              },
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
                // ...successStatus,
                // report: purchaseSummaryReportMock,
              })),
            );
          }),
        );
      }),
    );
  }

  getPurchaseSummaryDetails(reference: string): Observable<PurchaseSummaryDetailsRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('purchaseSummaryDetails', {
          urlParams: { userId },
          queryParams: { code: reference },
        });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                table: response.items || [],
              },
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
                // ...successStatus,
                // table: purchaseSummaryItemsMock,
              })),
            );
          }),
        );
      }),
    );
  }

  getCreditSummary(): Observable<CreditSummaryRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('creditSummary', { urlParams: { userId } });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                report: response,
              },
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  getPaymentHistory(config: PaymentHistoryRequestConfig): Observable<PaymentHistoryReportRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('paymentHistory', { urlParams: { userId }, queryParams: config });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                table: response,
              },
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
                // ...successStatus,
                // table: paymentHistoryMock
              })),
            );
          }),
        );
      }),
    );
  }

  getPaymentHistoryDetails(config: PaymentHistoryDetailsRequestConfig): Observable<PaymentHistoryDetailsRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('paymentHistoryDetails', {
          urlParams: { userId },
          queryParams: config,
        });

        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              ...{
                table: response.invoices,
              },
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
                // ...successStatus,
                // table: paymentHistoryDetailsMock
              })),
            );
          }),
        );
      }),
    );
  }
}
